
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PlusCircle, GripVertical, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface Project {
  id: string;
  title: string;
  technologies: string[] | null;
  created_at: string;
  published: boolean;
  display_order: number | null;
  [key: string]: any; // Allow for other properties
}

interface SortableItemProps {
  id: string;
  project: Project;
  onView: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  columns: Array<{key: string, label: string, format?: (value: any, row: any) => React.ReactNode}>;
}

const SortableItem = ({ id, project, onView, onEdit, onDelete, columns }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <TableCell className="w-[40px]">
        <div className="flex items-center justify-center cursor-grab" {...attributes} {...listeners}>
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>
      </TableCell>
      
      {columns.map((column) => (
        <TableCell key={`${project.id}-${column.key}`}>
          {column.format 
            ? column.format(project[column.key as keyof Project], project) 
            : project[column.key as keyof Project]}
        </TableCell>
      ))}
      
      <TableCell>
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm" onClick={() => onView(project)}>
            View
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(project)}>
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(project)}>
            Delete
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const ProjectsAdmin = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState("display_order");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProjects();
  }, [sortField, sortOrder]);

  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order(sortField, { ascending: sortOrder === "asc" });

      if (error) throw error;
      console.log("Fetched projects:", data);
      // Convert data to Project type with display_order
      const projectsWithDisplayOrder = data?.map(project => ({
        ...project,
        display_order: project.display_order || null
      })) || [];
      setProjects(projectsWithDisplayOrder);
    } catch (error: any) {
      toast({
        title: "Failed to load projects",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      // Update the UI order first
      const oldIndex = projects.findIndex((item) => item.id === active.id);
      const newIndex = projects.findIndex((item) => item.id === over.id);
      const reorderedItems = arrayMove(projects, oldIndex, newIndex);
      setProjects(reorderedItems);
      
      try {
        setIsSaving(true);
        
        // Create an array of updates with new display orders
        const updatedItems = reorderedItems.map((item, index) => ({
          id: item.id,
          display_order: index + 1
        }));
        
        // Update each item individually
        for (const item of updatedItems) {
          const { error } = await supabase
            .from('projects')
            .update({ display_order: item.display_order })
            .eq('id', item.id);
            
          if (error) {
            console.error("Error updating item:", error);
            throw error;
          }
        }
        
        toast({
          title: "Order updated",
          description: "Project display order has been saved",
        });
        
        // Fetch the updated data
        await fetchProjects();
      } catch (error: any) {
        toast({
          title: "Failed to update order",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDelete = async (project: any) => {
    if (!confirm("Are you sure you want to delete this project?")) return;

    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .match({ id: project.id });

      if (error) throw error;

      toast({
        title: "Project deleted",
        description: "The project has been successfully deleted.",
      });

      await fetchProjects();
    } catch (error: any) {
      toast({
        title: "Failed to delete project",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const columns = [
    {
      key: "title",
      label: "Title",
    },
    {
      key: "technologies",
      label: "Technologies",
      format: (value: string[] | null) => {
        if (!value || value.length === 0) return "None";
        return (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 3).map((tech, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tech}
              </Badge>
            ))}
            {value.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{value.length - 3} more
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "created_at",
      label: "Created",
      format: (value: string) => format(new Date(value), "MMM d, yyyy"),
    },
    {
      key: "published",
      label: "Status",
      format: (value: boolean) => (
        <Badge variant={value ? "green" : "secondary"}>
          {value ? "Published" : "Draft"}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Projects</h2>
        <Button
          size="sm"
          onClick={() => navigate("/projects/create")}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" /> Add Project
        </Button>
      </div>
      
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Sort by:</span>
          <Select
            value={sortField}
            onValueChange={setSortField}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort field" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="display_order">Custom Order</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="created_at">Creation Date</SelectItem>
              <SelectItem value="updated_at">Last Updated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Order:</span>
          <Select
            value={sortOrder}
            onValueChange={setSortOrder}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading projects...</div>
        </div>
      ) : (
        <div className="border rounded-md">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">Order</TableHead>
                  {columns.map((column) => (
                    <TableHead key={column.key}>{column.label}</TableHead>
                  ))}
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <SortableContext
                items={projects.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {projects.map((project) => (
                    <SortableItem
                      key={project.id}
                      id={project.id}
                      project={project}
                      onView={(project) => navigate(`/projects/${project.id}`)}
                      onEdit={(project) => navigate(`/projects/edit/${project.id}`)}
                      onDelete={handleDelete}
                      columns={columns}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </DndContext>
        </div>
      )}

      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md shadow-lg">
            <div className="animate-spin h-8 w-8 border-4 border-brand-green border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-center text-gray-700">Saving order...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsAdmin;
